import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { useDocumentTitle } from "../../utils/data";
import { useNavigate, Link } from "react-router-dom";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const offeringsData = [
  {
    icon: "🎯",
    title: "Tailored Solutions",
    description: "Custom-built products designed specifically for your unique business requirements and goals.",
  },
  {
    icon: "🔄",
    title: "End-to-End Support",
    description: "Comprehensive support from ideation to deployment and beyond, ensuring seamless execution.",
  },
  {
    icon: "⚡",
    title: "Advanced Technologies",
    description: "Leveraging cutting-edge technologies to build modern, efficient, and future-ready solutions.",
  },
  {
    icon: "📈",
    title: "Scalability & Flexibility",
    description: "Solutions that grow with your business, adapting to changing needs and market demands.",
  },
  {
    icon: "👥",
    title: "Talent & Expertise",
    description: "Access to skilled professionals with deep expertise across diverse technology domains.",
  },
  {
    icon: "⚙️",
    title: "Streamlined Processes",
    description: "Optimized workflows and methodologies for faster delivery and higher quality outcomes.",
  },
];

const productsData = [
  {
    title: "Ecommerce App",
    description: "Centralize clients, deals, and support with built-in automations and analytics.",
    emoji: "🛒",
    path: "/products/ecommerce-app",
  },
  {
    title: "Sport Reserve",
    description: "Book and manage courts, coaches, and events with real-time availability.",
    emoji: "🎾",
    path: "/products/sport-reserve",
  },
  {
    title: "Talent Flow",
    description: "ATS + onboarding + payroll workflows to hire fast and stay compliant.",
    emoji: "🧑‍💼",
    path: "/products/talent-flow",
  },
  {
    title: "Queue Bridge",
    description: "Appointment and queue management with live wait times and smart routing.",
    emoji: "📅",
    path: "/products/queue-bridge",
  },
];

const ProductsIndex = () => {
  useDocumentTitle("Briozon - Products");
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  OUR PRODUCTS
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Powerful Solutions for{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Your Growth
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  From vision to value, our cutting-edge products are built to meet your 
                  evolving needs and fuel your growth journey.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  📦
                </Box>

                {/* Floating product cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "15%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "28px" }}>🛒</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "14px" }}>
                    E-commerce
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "28px" }}>🎾</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "14px" }}>
                    Sports
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "8%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "28px" }}>🧑‍💼</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "14px" }}>
                    Talent
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "28px" }}>📅</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "14px" }}>
                    Queue
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Products Grid Section */}
      <Box
        sx={{
          py: { xs: 7, sm: 9, md: 12 },
          background: "linear-gradient(160deg, #f8faff 0%, #ffffff 50%, #faf5ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Dot grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.07) 1px, transparent 1px)",
            backgroundSize: "38px 38px",
            pointerEvents: "none",
          }}
        />
        {/* Glow orbs */}
        <Box sx={{ position: "absolute", top: "4%", right: "6%", width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,124,250,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "8%", left: "4%", width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
                border: "1px solid rgba(91,124,250,0.2)",
                borderRadius: "50px",
                px: 2.5,
                py: 0.8,
                mb: 2.5,
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", textTransform: "uppercase" }}>
                📦 Explore Our Products
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "30px", sm: "38px", md: "46px" },
                fontWeight: 800,
                color: "#1b1f33",
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              Built to{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Power Your Business
              </Box>
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", sm: "18px" }, color: "#4c5472", maxWidth: 560, mx: "auto", lineHeight: 1.8 }}>
              Four flagship products — each solving a unique challenge with smart technology and seamless UX ✨
            </Typography>
          </Box>

          {/* Products Grid */}
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {productsData.map((product, idx) => {
              const cfgs = [
                {
                  gradient: "linear-gradient(135deg, #93b4ff 0%, #67d9f0 100%)",
                  shadow: "rgba(147,180,255,0.45)",
                  features: ["🤖 AI Recommendations", "📊 Smart Analytics", "📦 Inventory Management", "🌐 Multi-channel Sales"],
                },
                {
                  gradient: "linear-gradient(135deg, #6ee7a0 0%, #5ee8d8 100%)",
                  shadow: "rgba(110,231,160,0.45)",
                  features: ["📅 Real-time Booking", "👨‍🏫 Coach Management", "🏆 Event Scheduling", "⚡ Live Availability"],
                },
                {
                  gradient: "linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)",
                  shadow: "rgba(196,181,253,0.45)",
                  features: ["📋 ATS System", "🎓 Onboarding Flow", "💰 Payroll Integration", "✅ Compliance Tools"],
                },
                {
                  gradient: "linear-gradient(135deg, #fdba74 0%, #fca5a5 100%)",
                  shadow: "rgba(253,186,116,0.45)",
                  features: ["🔄 Smart Queue", "⏱️ Live Wait Times", "🗺️ Smart Routing", "📱 Appointments"],
                },
              ];
              const cfg = cfgs[idx];
              const num = String(idx + 1).padStart(2, "0");

              return (
                <Grid item xs={12} sm={6} key={product.title} sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    onClick={() => navigate(product.path)}
                    sx={{
                      borderRadius: "24px",
                      overflow: "hidden",
                      background: "#fff",
                      boxShadow: "0 4px 24px rgba(17,24,39,0.07)",
                      border: "1px solid rgba(91,124,250,0.1)",
                      cursor: "pointer",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.35s ease, box-shadow 0.35s ease",
                      "&:hover": {
                        transform: "translateY(-14px)",
                        boxShadow: `0 28px 64px ${cfg.shadow}`,
                      },
                      "&:hover .prod-emoji": {
                        transform: "scale(1.18) rotate(-6deg)",
                      },
                      "&:hover .prod-num": {
                        opacity: 0.2,
                      },
                      "&:hover .prod-cta": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 12px 32px ${cfg.shadow}`,
                      },
                    }}
                  >
                    {/* Gradient top */}
                    <Box
                      sx={{
                        height: { xs: 210, sm: 240 },
                        background: cfg.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* Decorative circles inside top */}
                      <Box sx={{ position: "absolute", top: -30, left: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.08)", pointerEvents: "none" }} />
                      <Box sx={{ position: "absolute", bottom: -20, right: -20, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
                      {/* Ghost number */}
                      <Typography
                        className="prod-num"
                        sx={{
                          position: "absolute",
                          bottom: -16,
                          right: 20,
                          fontSize: "130px",
                          fontWeight: 900,
                          lineHeight: 1,
                          color: "rgba(255,255,255,0.1)",
                          userSelect: "none",
                          fontFamily: "monospace",
                          transition: "opacity 0.35s ease",
                        }}
                      >
                        {num}
                      </Typography>
                      {/* Big emoji */}
                      <Box
                        className="prod-emoji"
                        sx={{
                          fontSize: { xs: "84px", sm: "100px" },
                          filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.25))",
                          transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                          zIndex: 1,
                          lineHeight: 1,
                        }}
                      >
                        {product.emoji}
                      </Box>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: { xs: 3, sm: 4 }, flex: 1, display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: "#1b1f33", mb: 1, fontSize: { xs: "21px", sm: "25px" } }}
                      >
                        {product.title}
                      </Typography>
                      <Typography
                        sx={{ color: "#4c5472", lineHeight: 1.75, mb: 2.5, fontSize: { xs: "15px", sm: "16px" } }}
                      >
                        {product.description}
                      </Typography>

                      {/* Feature pills */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                        {cfg.features.map((f) => (
                          <Box
                            key={f}
                            sx={{
                              background: "linear-gradient(135deg, rgba(91,124,250,0.07) 0%, rgba(118,75,162,0.07) 100%)",
                              border: "1px solid rgba(91,124,250,0.14)",
                              borderRadius: "50px",
                              px: 1.6,
                              py: 0.55,
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#4c5472",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {f}
                          </Box>
                        ))}
                      </Box>

                      {/* CTA button */}
                      <Box sx={{ mt: "auto" }}>
                        <Button
                          fullWidth
                          className="prod-cta"
                          onClick={(e) => { e.stopPropagation(); navigate(product.path); }}
                          sx={{
                            background: cfg.gradient,
                            color: "#fff",
                            fontWeight: 700,
                            borderRadius: "13px",
                            py: 1.4,
                            fontSize: "15px",
                            textTransform: "none",
                            boxShadow: `0 6px 20px ${cfg.shadow}`,
                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                          }}
                        >
                          Explore {product.title} →
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Innovative Solutions Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          backgroundColor: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91, 124, 250, 0.08), rgba(91, 124, 250, 0.02))",
            top: -50,
            right: -50,
            animation: `${floatBall} 10s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118, 75, 162, 0.08), rgba(118, 75, 162, 0.02))",
            bottom: 50,
            left: -30,
            animation: `${floatBall} 8s ease-in-out infinite`,
            animationDelay: "-3s",
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="overline"
              sx={{
                color: "#5b7cfa",
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: "block",
              }}
            >
              OUR PRODUCT OFFERINGS
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Innovative Solutions to{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Build Your Vision
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {offeringsData.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "16px",
                    p: { xs: 3, md: 4 },
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    position: "relative",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 40px rgba(91, 124, 250, 0.15)",
                      backgroundColor: "#fff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.8rem",
                      mb: 2,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1b1f33",
                      mb: 1,
                      fontSize: { xs: "20px", sm: "22px" },
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#4c5472",
                      lineHeight: 1.7,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Partner CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#fff",
                mb: 2,
                fontSize: { xs: "1.6rem", sm: "2rem", md: "2.2rem" },
              }}
            >
              Partner with Us for Success
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: 650,
                mx: "auto",
                lineHeight: 1.8,
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              Ready to take the next step in building your product? Contact us today, 
              and let's discuss how we can turn your ideas into reality with our tailored solutions.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.9)",
                },
              }}
            >
              Contact Us Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ProductsIndex;
